import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Modal,
  Badge,
  Row,
  Col,
  ProgressBar,
  Alert,
} from "react-bootstrap";
import Loader from "../../components/common/Loader";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Eye,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  Award,
  FileText,
  Filter,
  X,
  ChevronDown,
} from "react-feather";

const StudentManager = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [studentDetails, setStudentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [stats, setStats] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const navigate = useNavigate();

  // New filter states
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedCity, setSelectedCity] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [availableCities, setAvailableCities] = useState([]);

  // Updated constants for new scoring system
  const MAX_TOTAL_SCORE = 91; // Updated from 175 to 92
  const HIGH_THRESHOLD = 73; // 70+ points is High
  const MEDIUM_THRESHOLD = 46; // 50-69 points is Medium

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (students.length > 0) {
      calculateStats();
      extractUniqueCities();
    }
  }, [students]);

  const fetchStudents = async () => {
    try {
      const response = await api.get("/students/admin/all");
      if (response.data.success) {
        setStudents(response.data.students);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (studentId) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/students/${studentId}`);
      if (response.data.success) {
        setStudentDetails(response.data.student);
        setShowDetails(true);
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
      alert("Failed to load student details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const calculateStats = () => {
    const total = students.length;

    // Updated to use the new 91-point scoring thresholds
    const high = students.filter((s) => {
      if (!s.totalScore) return false;
      return s.totalScore >= HIGH_THRESHOLD; // 73+ points
    }).length;

    const medium = students.filter((s) => {
      if (!s.totalScore) return false;
      return s.totalScore >= MEDIUM_THRESHOLD && s.totalScore <= 72; // 46-72 points
    }).length;

    const low = students.filter((s) => {
      if (!s.totalScore) return false;
      return s.totalScore <= 45; // 0-45 points
    }).length;

    setStats({ total, high, medium, low });
  };

  const extractUniqueCities = () => {
    const cities = [...new Set(students.map(s => s.city).filter(Boolean))];
    setAvailableCities(cities.sort());
  };

  const clearFilters = () => {
    setDateRange({ start: "", end: "" });
    setSelectedCity("");
    setSortBy("newest");
    setSearchTerm("");
  };

  const filteredStudents = students
    .filter((student) => {
      // Search filter
      const matchesSearch = 
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.appointmentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.city?.toLowerCase().includes(searchTerm.toLowerCase());

      // City filter
      const matchesCity = !selectedCity || student.city === selectedCity;

      // Date range filter
      let matchesDateRange = true;
      if (dateRange.start && dateRange.end && student.createdAt) {
        const studentDate = new Date(student.createdAt);
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999); // Include the entire end day
        matchesDateRange = studentDate >= startDate && studentDate <= endDate;
      } else if (dateRange.start && student.createdAt) {
        const studentDate = new Date(student.createdAt);
        const startDate = new Date(dateRange.start);
        matchesDateRange = studentDate >= startDate;
      } else if (dateRange.end && student.createdAt) {
        const studentDate = new Date(student.createdAt);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        matchesDateRange = studentDate <= endDate;
      }

      return matchesSearch && matchesCity && matchesDateRange;
    })
    .sort((a, b) => {
      // Sorting functionality
      switch (sortBy) {
        case "newest":
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case "oldest":
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case "name-asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name-desc":
          return (b.name || "").localeCompare(a.name || "");
        case "score-high":
          return (b.totalScore || 0) - (a.totalScore || 0);
        case "score-low":
          return (a.totalScore || 0) - (b.totalScore || 0);
        default:
          return 0;
      }
    });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewClick = (studentId) => {
    navigate(`/admin/students/${studentId}`);
  };

  const handleViewModal = async (student) => {
    setSelectedStudent(student);
    await fetchStudentDetails(student._id);
  };

  // Updated score badge class for new 92-point scale
  const getScoreBadgeClass = (score) => {
    if (!score) return "secondary";
    if (score >= HIGH_THRESHOLD) return "success"; // 73+ is High
    if (score >= MEDIUM_THRESHOLD) return "warning"; // 46-72 is Medium
    return "danger"; // 0-45 is Low
  };
  
  // Updated score progress calculation for 92-point scale
  const getScoreProgress = (score) => {
    if (!score) return 0;
    return Math.min((score / MAX_TOTAL_SCORE) * 80, 100);
  };
  
  const getProbabilityBadgeClass = (level) => {
    if (!level) return "secondary";
    if (level === "High") return "success";
    if (level === "Medium") return "warning";
    if (level === "Low") return "danger";
    return "secondary";
  };

  const getProbabilityColor = (level) => {
    if (level === "High") return "#28a745";
    if (level === "Medium") return "#ffc107";
    if (level === "Low") return "#dc3545";
    return "#6c757d";
  };

  const handleExportCSV = () => {
    // Create CSV headers
    const headers = [
      'Name',
      'Email',
      'Phone',
      'City',
      'Student ID',
      'Total Score',
      'Probability Level',
      'Created Date'
    ];

    // Prepare data
    const csvData = filteredStudents.map(student => [
      student.name || 'N/A',
      student.email || 'N/A',
      student.phone || 'N/A',
      student.city || 'N/A',
      student.appointmentId || 'N/A',
      student.totalScore || 0,
      student.probabilityLevel || 'N/A',
      formatDate(student.createdAt)
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <Loader />;

  const pageStyle = {
    minHeight: "100vh",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  };

  const statCardStyle = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    borderRadius: "12px",
    border: "none",
  };

  const tableRowStyle = {
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const searchStyle = {
    borderRadius: "10px",
    border: "2px solid #e0e0e0",
    padding: "12px 20px",
    fontSize: "16px",
  };

  const filterSectionStyle = {
    background: "#f8f9fa",
    borderRadius: "10px",
    padding: "20px",
    marginBottom: "20px",
  };

  return (
    <div style={pageStyle}>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold text-gradient">
                <User size={36} className="me-3" />
                Student Management
              </h2>
              <p className="text-black-50 mb-0" style={{ fontSize: "1.1rem" }}>
                Manage and track all student applications and assessments
              </p>
            </div>
          </div>

          {/* Search Card */}
          <Card className="mb-4" style={cardStyle}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center gap-3">
                <div style={{ flex: 1 }}>
                  <InputGroup
                    style={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <InputGroup.Text
                      style={{
                        background: "#f8f9fa",
                        border: "none",
                        padding: "0 20px",
                      }}
                    >
                      <Search size={20} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search students by name, email, city, appointment ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={searchStyle}
                      className="border-0"
                    />
                  </InputGroup>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2"
                    style={{ borderRadius: "10px", fontWeight: "600" }}
                  >
                    <Filter size={18} className="me-2" />
                    Filters
                    {showFilters ? <ChevronDown size={18} className="ms-2" /> : <ChevronDown size={18} className="ms-2" />}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleExportCSV}
                    className="px-4 py-2"
                    style={{ borderRadius: "10px", fontWeight: "600" }}
                  >
                    <Download size={18} className="me-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <div style={filterSectionStyle} className="mt-4">
                  <Row className="align-items-end">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Date Range</Form.Label>
                        <Form.Control
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                          className="mb-2"
                        />
                        <Form.Control
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">City</Form.Label>
                        <Form.Select
                          value={selectedCity}
                          onChange={(e) => setSelectedCity(e.target.value)}
                        >
                          <option value="">All Cities</option>
                          {availableCities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Sort By</Form.Label>
                        <Form.Select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="name-asc">Name (A-Z)</option>
                          <option value="name-desc">Name (Z-A)</option>
                          <option value="score-high">Score (High to Low)</option>
                          <option value="score-low">Score (Low to High)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Button
                        variant="outline-secondary"
                        onClick={clearFilters}
                        className="w-100"
                        style={{ borderRadius: "10px" }}
                      >
                        <X size={16} className="me-2" />
                        Clear Filters
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Students Table */}
          <Card style={cardStyle}>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <tr>
                      <th
                        className="ps-4 py-3"
                        style={{ borderTopLeftRadius: "15px" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <User size={16} />
                          Student
                        </div>
                      </th>
                      <th className="py-3">Contact</th>
                      <th className="py-3">City</th>
                      <th className="py-3">Progress</th>
                      <th className="py-3">Date</th>
                      <th className="py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr
                        key={student._id}
                        style={tableRowStyle}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "translateX(5px)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "translateX(0)")
                        }
                      >
                        <td className="ps-4">
                          <div className="d-flex align-items-start gap-3">
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              {student.name?.charAt(0) || "?"}
                            </div>
                            <div>
                              <div className="fw-bold mb-1">
                                {student.name || "N/A"}
                              </div>
                              <small className="text-muted">
                                ID: {student.appointmentId || "N/A"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <Mail size={14} className="text-muted" />
                              <small>{student.email || "N/A"}</small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Phone size={14} className="text-muted" />
                              <small>{student.phone || "N/A"}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <small>{student.city || "N/A"}</small>
                        </td>
                        <td>
                          <div style={{ maxWidth: "150px" }}>
                            <ProgressBar
                              now={getScoreProgress(student.totalScore)}
                              variant={getScoreBadgeClass(student.totalScore)}
                              style={{
                                height: "20px",
                                borderRadius: "4px",
                                marginTop: "15px",
                              }}
                              label={`${Math.round(getScoreProgress(student.totalScore))}%`}
                            />
                          </div>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Calendar size={14} className="text-muted" />
                            <div>
                              <div className="fw-medium">
                                {formatDate(student.createdAt)}
                              </div>
                              <small className="text-muted">Created</small>
                            </div>
                          </div>
                        </td>
                        <td className="text-end pe-4">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewClick(student._id)}
                            className="d-flex align-items-center gap-2 px-3 py-2"
                            style={{ borderRadius: "8px" }}
                          >
                            <Eye size={14} />
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="py-4">
                            <Search size={48} className="text-muted mb-3" />
                            <h5 className="text-muted">No students found</h5>
                            <p className="text-muted mb-0">
                              Try adjusting your search or filter criteria
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Footer Info */}
          <div className="mt-4 text-center">
            <p className="text-black-50">
              Showing <strong>{filteredStudents.length}</strong> of{" "}
              <strong>{students.length}</strong> students
            </p>
          </div>
        </div>

        {/* Student Details Modal */}
        <Modal
          show={showDetails}
          onHide={() => setShowDetails(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton style={{ borderBottom: "none" }}>
            <Modal.Title className="fw-bold">
              {selectedStudent?.name} - Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingDetails ? (
              <Loader />
            ) : studentDetails ? (
              <div>
                {/* Detailed student info would go here */}
                <p>
                  Detailed view would show complete student information and
                  assessment results.
                </p>
              </div>
            ) : (
              <Alert variant="warning">Could not load student details.</Alert>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default StudentManager;